import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { courseService } from "@/services/courseService";
import { apiError } from "@/lib/api";
import { useGenerateCourse } from "@/hooks/useCourses";
import { useDojo, dojo } from "@/plugins/vibeDojo/dojoAudio";
import { useDojoMode } from "@/plugins/vibeDojo/DojoContext";
import { useGuide } from "@/plugins/vibeDojo/useGuide";
import { Typewriter } from "@/plugins/vibeDojo/Typewriter";
import { BreathingSession } from "@/plugins/vibeDojo/BreathingSession";
import { Button } from "@/components/ui/button";
import { Search, Plus, Zap, Coffee, Layers, PlayCircle, X, Volume2, Sprout, Check, Loader2, Wind } from "lucide-react";

const LOADING_MSGS = [
  "Researching your topic…",
  "Designing the curriculum…",
  "Writing the script…",
  "Structuring chapters…",
  "Finalizing your course…",
];

export default function Dojo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enabled, enable, disable, volume, setVolume } = useDojo();
  const { sceneId, SCENES, setScene, setDojoMode } = useDojoMode();
  const { name: guideName, image: guideImage } = useGuide();

  const [topic, setTopic] = useState("");
  const [length, setLength] = useState("overview");
  const [msgIdx, setMsgIdx] = useState(0);
  const [breathing, setBreathing] = useState(false);

  const { data: continueCourse } = useQuery({
    queryKey: ["continue-course"],
    queryFn: courseService.getContinue,
    staleTime: 30_000,
  });

  const generateMutation = useGenerateCourse({
    onSuccess: (data) => navigate(`/course/${data.course.id}`),
    onError: (e) => {
      toast.error(apiError(e));
      if (e?.response?.status === 402) navigate("/pricing");
    },
  });
  const generating = generateMutation.isPending;

  // Turn on the global, persistent Dojo Mode when entering the Dojo.
  useEffect(() => {
    setDojoMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!generating) return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length), 1800);
    return () => clearInterval(t);
  }, [generating]);

  const exitDojo = () => {
    setDojoMode(false);
    navigate(user ? "/dashboard" : "/");
  };

  // Launch generation inline — stay in the Dojo, then go straight to the course.
  const start = () => {
    const t = topic.trim();
    if (!t) { toast.error("Enter a topic to begin"); return; }
    if (!user) { toast.error("Create a free account to generate your course"); navigate("/register"); return; }
    generateMutation.mutate({ topic: t, length });
  };

  return (
    <div className="min-h-screen dojo-page text-[#FAFAFA] relative" data-testid="dojo-page">
      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-8 h-16">
        <div className="flex items-center gap-2 text-emerald-300/90 font-display font-bold tracking-wide">
          <Sprout className="h-5 w-5" /> The Dojo
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => (enabled ? disable() : enable())}
            className={`grid place-items-center h-9 w-9 rounded-full border transition-all ${enabled ? "border-emerald-400/60 text-emerald-300 bg-emerald-400/10" : "border-white/25 text-white/70 hover:text-white"}`}
            data-testid="dojo-ambient-toggle" title="Ambient sound"
          >
            <Sprout className="h-4 w-4" />
          </button>
          {enabled && (
            <div className="hidden sm:flex items-center gap-1.5 w-[110px]">
              <Volume2 className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
              <input type="range" min="0" max="1" step="0.05" value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-full h-1 accent-emerald-400 cursor-pointer" data-testid="dojo-volume" />
            </div>
          )}
          <button onClick={exitDojo}
            className="inline-flex items-center gap-1.5 border border-white/25 hover:border-white/60 hover:bg-white/10 px-3 h-9 text-sm font-semibold transition-colors"
            data-testid="dojo-exit">
            <X className="h-4 w-4" /> Exit
          </button>
        </div>
      </div>

      {/* Center: serene creator */}
      <div className="relative z-10 min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-2xl text-center"
        >
          {generating ? (
            <div className="flex flex-col items-center py-10" data-testid="dojo-generating">
              <Loader2 className="h-10 w-10 text-emerald-300 animate-spin mb-6" />
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Preparing your lesson…</h2>
              <p className="text-white/85">{LOADING_MSGS[msgIdx]}</p>
              <p className="mt-4 text-sm text-white/70">Crafting "{topic}" — stay a moment, breathe.</p>
            </div>
          ) : (
          <>
          {/* Wise Mentor guide — portrait + typewriter greeting (synced from Admin) */}
          <div className="flex flex-col items-center mb-7" data-testid="dojo-guide">
            <div className="relative">
              <img src={guideImage} alt={guideName} data-testid="dojo-guide-image"
                className="h-24 w-24 sm:h-28 sm:w-28 rounded-full object-cover ring-2 ring-emerald-400/40 shadow-[0_0_40px_-12px_rgba(16,185,129,0.7)]" />
              <button
                onClick={() => { dojo.startBreathing(); setBreathing(true); }}
                data-testid="dojo-breath-button"
                title="Take a breath"
                className="group absolute -bottom-1 -right-1 grid place-items-center h-9 w-9 rounded-full bg-emerald-500 text-[#06120c] shadow-[0_0_0_4px_rgba(5,11,9,0.6)] hover:bg-emerald-400 transition-colors animate-pulse-glow"
              >
                <Wind className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-4 text-sm sm:text-base text-emerald-100/90 max-w-md min-h-[2.5rem]" data-testid="dojo-guide-greeting">
              <Typewriter text={`I am ${guideName}. Tell me what you wish to learn, and I shall light the path.`} speed={38} />
            </p>
          </div>
          {continueCourse && (
            <button
              onClick={() => navigate(`/course/${continueCourse.id}`)}
              className="w-full mb-5 text-left group border border-emerald-400/30 bg-emerald-400/5 hover:bg-emerald-400/10 hover:border-emerald-400/60 backdrop-blur-sm px-5 py-4 transition-colors"
              data-testid="dojo-continue"
            >
              <div className="flex items-center gap-4">
                <PlayCircle className="h-6 w-6 text-emerald-300 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-300/90">Continue mastering</p>
                  <p className="font-display font-bold truncate">{continueCourse.title}</p>
                </div>
                <span className="text-sm font-semibold text-emerald-300">Resume →</span>
              </div>
            </button>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="w-full sm:flex-1 flex items-center gap-2 border border-white/30 bg-black/45 backdrop-blur-md px-4 h-14 focus-within:border-emerald-400/70 transition-colors">
              <Search className="h-4 w-4 text-white/70 shrink-0" />
              <input value={topic} onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && start()}
                placeholder="LearnMe About e.g. Stoicism, the art of tea, quantum basics…"
                className="flex-1 bg-transparent outline-none text-base text-white placeholder:text-white/60" data-testid="dojo-topic-input" />
            </div>
            <Button onClick={start} variant="ghost" className="rounded-none h-14 px-7 w-full sm:w-auto bg-emerald-400/15 border border-emerald-400 text-white hover:bg-emerald-400/25 hover:text-white gap-2 font-normal text-lg shrink-0" data-testid="dojo-create-button">
              <Plus className="h-5 w-5" /> Begin
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            {[
              { key: "overview", icon: Zap, label: "2 min" },
              { key: "lunch", icon: Coffee, label: "15 min" },
              { key: "comprehensive", icon: Layers, label: "Full training" },
            ].map((l) => (
              <button key={l.key} onClick={() => setLength(l.key)} data-testid={`dojo-length-${l.key}`}
                className={`inline-flex items-center gap-1.5 px-3 h-9 border text-sm font-semibold transition-colors ${length === l.key ? "border-emerald-400 text-emerald-200 bg-emerald-400/15" : "border-white/30 text-white/80 hover:text-white"}`}>
                <l.icon className="h-4 w-4" /> {l.label}
              </button>
            ))}
          </div>
          </>
          )}
        </motion.div>
      </div>

      {/* Scene picker (bottom) */}
      <div className="fixed bottom-0 left-0 right-0 z-10 px-6 py-4 flex items-center justify-center gap-3" data-testid="dojo-scene-picker">
        {SCENES.map((s) => (
          <button key={s.id} onClick={() => setScene(s.id)} title={s.name} data-testid={`dojo-scene-${s.id}`}
            className={`relative h-11 w-16 overflow-hidden border-2 transition-all ${sceneId === s.id ? "border-emerald-400 scale-105" : "border-white/25 opacity-70 hover:opacity-100"}`}>
            <img src={s.src} alt={s.name} className="w-full h-full object-cover" />
            {sceneId === s.id && (
              <span className="absolute inset-0 grid place-items-center bg-black/30">
                <Check className="h-4 w-4 text-emerald-300" />
              </span>
            )}
          </button>
        ))}
      </div>

      {breathing && <BreathingSession onClose={() => setBreathing(false)} />}
    </div>
  );
}
